import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { FileText, CheckCircle2, DollarSign, Calendar } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const eur = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(v) || 0);

export default function PayrollPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [genOpen, setGenOpen] = useState(false);
  const [genMonth, setGenMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    const endpoint = isAdmin ? `/payroll?month=${month}` : `/payroll/me?month=${month}`;
    const { data } = await http.get(endpoint);
    setRows(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const generate = async () => {
    try {
      const { data } = await http.post("/payroll/generate", { month: genMonth });
      toast.success(t("payroll.generated", { n: data.items.length }));
      setGenOpen(false); setMonth(genMonth); load();
    } catch (e) { toast.error("Generate failed"); }
  };
  const approve = async (id) => { try { await http.post(`/payroll/${id}/approve`); toast.success("Approved"); load(); } catch { toast.error("Failed"); } };
  const markPaid = async (id) => { try { await http.post(`/payroll/${id}/mark-paid`); toast.success("Marked paid"); load(); } catch { toast.error("Failed"); } };
  const payslip = async (id) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.access_token");
    const r = await fetch(`${base}/api/payroll/${id}/payslip.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error("PDF failed");
    const blob = await r.blob(); window.open(URL.createObjectURL(blob), "_blank");
  };

  return (
    <PageChrome title={t("payroll.title")} subtitle={t("payroll.subtitle")} testId="payroll-page">
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3"/>{t("payroll.month")}: <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-44 bg-white"/></label>
        {isAdmin && (<div className="ml-auto"><Button onClick={()=>setGenOpen(true)} className="bg-[#0A1F44]" data-testid="payroll-generate-btn">{t("payroll.generateMonth")}</Button></div>)}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="payroll-table">
          <thead className="bg-slate-50 text-xs uppercase"><tr>
            <th className="text-left px-3 py-2">{t("payroll.staff")}</th><th className="text-left px-3 py-2">{t("payroll.month")}</th>
            <th className="text-right px-3 py-2">{t("payroll.hours")}</th><th className="text-right px-3 py-2">{t("payroll.rate")}</th>
            <th className="text-right px-3 py-2">{t("payroll.gross")}</th><th className="text-right px-3 py-2">{t("payroll.net")}</th>
            <th className="text-left px-3 py-2">{t("payroll.statusLabel")}</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody className="divide-y">
            {rows.map(r => (
              <tr key={r.id} data-testid={`payroll-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-3 py-2">{r.staff_name}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.month}</td>
                <td className="px-3 py-2 text-right">{r.hours_worked}h</td>
                <td className="px-3 py-2 text-right">{eur(r.hourly_rate)}/h</td>
                <td className="px-3 py-2 text-right font-medium">{eur(r.gross_total)}</td>
                <td className="px-3 py-2 text-right font-semibold text-[#C8102E]">{eur(r.net_total)}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs ${r.status==='paid'?'bg-emerald-100 text-emerald-700':r.status==='approved'?'bg-sky-100 text-sky-700':'bg-slate-100'}`}>{r.status}</span></td>
                <td className="px-3 py-2 text-right">
                  {isAdmin && r.status === "draft" && <Button size="sm" variant="ghost" onClick={()=>approve(r.id)} data-testid={`payroll-approve-${r.id}`}><CheckCircle2 className="h-4 w-4 text-emerald-600"/></Button>}
                  {isAdmin && r.status === "approved" && <Button size="sm" variant="ghost" onClick={()=>markPaid(r.id)} data-testid={`payroll-pay-${r.id}`}><DollarSign className="h-4 w-4 text-sky-600"/></Button>}
                  <Button size="sm" variant="ghost" onClick={()=>payslip(r.id)} data-testid={`payroll-pdf-${r.id}`}><FileText className="h-4 w-4"/></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400">{t("common.noResults")}</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("payroll.generateMonth")}</DialogTitle></DialogHeader>
          <div><label className="text-xs">{t("payroll.month")}</label><Input type="month" value={genMonth} onChange={(e)=>setGenMonth(e.target.value)} data-testid="payroll-gen-month"/></div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setGenOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={generate} className="bg-[#0A1F44]" data-testid="payroll-gen-confirm">{t("common.confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageChrome>
  );
}
